import { useState, useEffect, useMemo } from 'react'
import { Select } from 'antd'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts'
import type { PracticeAgentReportData } from '../../types'
import { classifyColumns, pickLabelColumn, deriveChartDataForDimension, formatFieldDisplayName, getBarAxisMaxFallback, getBarAxisMin } from '../../utils'
import { PaginationBar } from './PaginationBar'
import { productThemes } from '../../../../lib/productBranding'

export function GroupedBarViz({ data, onSelect, onPageChange, onGroupByChange }: { data: PracticeAgentReportData; onSelect?: (field: string, value: string) => void; onPageChange?: (p: number) => void; onGroupByChange?: (dim: string) => void }) {
  const { dimensionCols } = useMemo(() => classifyColumns(data.columns, data.rows), [data.columns, data.rows])
  const availableDimensions = data.dimensions?.length ? data.dimensions : dimensionCols
  const defaultDimension = useMemo(
    () => data.groupByDimension ?? pickLabelColumn(dimensionCols, data.rows),
    [data.groupByDimension, dimensionCols, data.rows],
  )
  const [selectedDimension, setSelectedDimension] = useState<string | null>(defaultDimension)
  useEffect(() => { setSelectedDimension(defaultDimension) }, [defaultDimension])

  const chartData = useMemo(() => {
    if (!selectedDimension) return data.chartData
    const derived = deriveChartDataForDimension(data.columns, data.rows, selectedDimension)
    return derived.length > 0 ? derived : data.chartData
  }, [data.columns, data.rows, data.chartData, selectedDimension])

  // Memoize expensive transformations so they don't re-run on every render.
  const { seriesNames, rechartsData } = useMemo(() => {
    const firstWithSeries = chartData.find((p) => p.series && p.series.length > 0)
    const names = firstWithSeries?.series?.map((s) => s.name) ?? []
    const rows = chartData.map((point) => {
      const obj: Record<string, string | number> = { label: point.label }
      for (const s of point.series ?? []) obj[s.name] = s.value
      return obj
    })
    return { seriesNames: names, rechartsData: rows }
  }, [chartData])

  if (chartData.length === 0) return <p className="text-sm text-gray-400 py-4">No chart data available.</p>

  const barHeight = 38
  const chartHeight = Math.max(160, rechartsData.length * barHeight + 48)


  const barColors = generateGroupedBarColors(seriesNames.length);
  const values = chartData.flatMap((point) => point.series?.map((s) => s.value) ?? [point.value])
  const xMin = getBarAxisMin(values)
  const xMaxFallback = getBarAxisMaxFallback(values)


  /**
   * Returns N colors similar in tone to the base color
   * but distinct enough to differentiate.
   */
  function generateGroupedBarColors(count: number): string[] {
    if (count <= 0) return [];
    const baseColor = productThemes.pracIntel.primary;
    const baseHsl = hexToHsl(baseColor);

    // How far we rotate around the hue wheel (degrees)
    const spread = 60; // total hue range to use (±30°)

    const colors: string[] = [];

    for (let i = 0; i < count; i++) {
      const t = count === 1 ? 0 : i / (count - 1); // 0 → 1

      const hueShift = (t - 0.5) * spread;
      const h = (baseHsl.h + hueShift + 360) % 360;

      // Slight variations for distinction
      const s = clamp(baseHsl.s + (t - 0.5) * 10, 40, 90);
      const l = clamp(baseHsl.l + (t - 0.5) * 8, 35, 75);

      colors.push(hslToHex(h, s, l));
    }

    return colors;
  }

  function clamp(v: number, min: number, max: number) {
  return Math.min(max, Math.max(min, v));
}

function hexToHsl(hex: string) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;

  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }

  if (h < 0) h += 360;

  const l = (max + min) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));

  return {
    h,
    s: s * 100,
    l: l * 100
  };
}

  function hslToHex(h: number, s: number, l: number): string {
    s /= 100;
    l /= 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;

    let r = 0, g = 0, b = 0;

    if (h < 60) [r, g, b] = [c, x, 0];
    else if (h < 120) [r, g, b] = [x, c, 0];
    else if (h < 180) [r, g, b] = [0, c, x];
    else if (h < 240) [r, g, b] = [0, x, c];
    else if (h < 300) [r, g, b] = [x, 0, c];
    else [r, g, b] = [c, 0, x];

    const toHex = (v: number) =>
      Math.round((v + m) * 255)
        .toString(16)
        .padStart(2, "0");

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  return (
    <div>
      {availableDimensions.length > 1 && (
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs text-gray-500">Group by</span>
          <Select
            size="small"
            value={selectedDimension ?? undefined}
            onChange={(dim) => { setSelectedDimension(dim); onGroupByChange?.(dim) }}
            options={availableDimensions.map((col) => ({ value: col, label: formatFieldDisplayName(col) }))}
            style={{ minWidth: 140 }}
          />
        </div>
      )}
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart data={rechartsData} layout="vertical" margin={{ top: 4, right: 24, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11 }} domain={[xMin, data.axisMaxValue != null ? data.axisMaxValue : xMaxFallback]} />
          <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={110} />
          <RechartsTooltip />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {seriesNames.map((name, i) => (
            <Bar
              key={name}
              dataKey={name}
              name={formatFieldDisplayName(name)}
              fill={barColors[i % barColors.length]}
              radius={[0, 3, 3, 0]}
              isAnimationActive={false}
              cursor={onSelect ? 'pointer' : undefined}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onClick={(barData: any) => onSelect?.(selectedDimension ?? '', barData.label)}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
      {onPageChange && data.totalRows != null && data.page != null && data.pageSize != null && (
        <PaginationBar
          page={data.page}
          totalRows={data.totalRows}
          pageSize={data.pageSize}
          onPageChange={onPageChange}
        />
      )}
    </div>
  )
}
